import { estDemo, type DemoEntrees } from "@hakili/app-shell";
import { t } from "@hakili/i18n";

import { VUES, VUE_DEFINITIONS, type Vue } from "./views.js";

/**
 * `P0-UIK-018` and `WF-CON-0`: the console's `Vue` switcher, behind the flag.
 *
 * 03, 2.0 calls it what it is: *"the bottom `Vue` bar in the mockup is a
 * demonstration device; in production the view is derived from the
 * authenticated user's role."* `WF-CON-0` adds that it *"exists only behind the
 * `demo` flag"*.
 *
 * **Danger sentence.** This component is a privilege escalation with a nice
 * design. The view decides which screens a person can reach, and a bar that
 * lets anyone pick `admin` turns 03, 2.0's table from a property of the user
 * into a dropdown , the accounts screen, the supervision screen and the
 * financial data behind them, one tap away, with the audit trail recording that
 * the signed-in person did it.
 *
 * So it renders **nothing** unless `estDemo` says both conditions hold, and it
 * asks that question rather than reading a boolean: a screen that reads the
 * server's flag directly gets the half that can be edited.
 *
 * **The switch changes the VIEW, never the rights.** 6.1.8's four layers run on
 * the server and answer to the JWT's role, so a demonstration switch to `admin`
 * shows the admin's screens and still cannot read one row the person may not
 * see. That is what makes the device safe to demonstrate and useless as an
 * attack , and it is a property of the server, not of this file.
 */

export interface VueSwitcherProps {
  readonly demo: DemoEntrees;
  readonly vue: Vue;
  readonly choisir: (vue: Vue) => void;
}

export function VueSwitcher({ demo, vue, choisir }: VueSwitcherProps): React.JSX.Element | null {
  // Returned before anything is built, so a production build has no switcher to
  // render rather than a hidden one.
  if (!estDemo(demo)) return null;

  return (
    <nav
      aria-label={t("console.demo.vue")}
      data-testid="vue-switcher"
      className="flex flex-wrap gap-2 border-t border-gray-200 bg-white p-3"
    >
      {VUES.map((candidate) => (
        <button
          key={candidate}
          type="button"
          aria-current={candidate === vue ? "true" : undefined}
          onClick={() => {
            choisir(candidate);
          }}
          className={`rounded-full px-3 py-1 text-xs ${
            candidate === vue ? "bg-indigo-600 text-white" : "bg-gray-50 text-gray-500"
          }`}
        >
          {t(VUE_DEFINITIONS[candidate].labelKey)}
        </button>
      ))}
    </nav>
  );
}
